import {
  type ReactElement,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';

import {
  elevatedFindingCount,
  findingsForRun,
  formatConfidence,
  formatCostUsd,
  formatDurationMs,
  formatIsoDateTime,
  payloadEntries,
  payloadPreview,
  resolveTargetName,
  runsForTick,
  ticksForSchedule,
  tokenCountLabel,
} from './groupWatchRuns';
import { mockPulseNow } from './marketingOpsSelectors';
import {
  clampTimelineRange,
  filterCardsByFoundDays,
  filterRunIssueCards,
  fullTimelineRange,
  issueMapMarkers,
  issueMarkCounts,
  runAnalysisFilterOptions,
  runAnalysisKpis,
  runIssueCardsInWindow,
  severityAgentCounts,
  severityHeatRows,
  timelineDaysEndingAt,
  uniqueRunsFromCards,
} from './runAnalysisSelectors';
import {
  WatchTowerShell,
} from './TargetedMarketingChrome';
import { findingSourceKind, findingSourceLabel } from './topicWatchAgents';
import useWatchTowerMock from './useWatchTowerMock';
import WatchTowerRunAnalysis from './WatchTowerRunAnalysis';
import { formatAgentType } from './watchTowerSelectors';

import type {
  RunAnalysisSpanDays,
  RunIssueCard,
  TimelineRange,
} from './runAnalysisSelectors';
import type { WatchFinding, WatchRun } from './types';

import styles from './scss/TargetedMarketing.module.scss';

const KIND_I18N: Record<string, string> = {
  company: 'kindCompany',
  product: 'kindProduct',
  building: 'kindBuilding',
  org_node: 'kindOrgNode',
  topic: 'kindTopic',
};

function statusClass(status: string): string {
  if (status === 'completed') {
    return styles.statusActive;
  }
  if (status === 'failed') {
    return styles.statusFailed;
  }
  if (status === 'running' || status === 'in_progress' || status === 'partial') {
    return styles.statusDraft;
  }
  return styles.statusArchived;
}

function drillLevel(
  scheduleExists: boolean,
  scheduledAt: string,
  runExists: boolean,
): 1 | 2 | 3 | 4 {
  if (runExists) {
    return 4;
  }
  if (scheduleExists && scheduledAt) {
    return 3;
  }
  if (scheduleExists) {
    return 2;
  }
  return 1;
}

function severityClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') {
    return styles.severityError;
  }
  if (severity === 'medium') {
    return styles.severityWarning;
  }
  return styles.muted;
}

function WatchTowerRuns(): ReactElement {
  const { t } = useTranslation();
  const { state } = useWatchTowerMock();
  const [params, setParams] = useSearchParams();

  const agentType = params.get('agent') || '';
  const scheduleId = Number(params.get('scheduleId')) || 0;
  const scheduledAt = params.get('scheduledAt') || '';
  const runId = Number(params.get('runId')) || 0;
  const findingPublicId = params.get('finding') || '';
  const [windowDays, setWindowDays] = useState<RunAnalysisSpanDays>(7);
  const [country, setCountry] = useState('');
  const [severity, setSeverity] = useState('');
  const [runStatus, setRunStatus] = useState('');
  const [timelineRange, setTimelineRange] = useState<TimelineRange>([0, 6]);

  const schedule = state.schedules.find((item) => item.id === scheduleId);
  const run = state.runs.find((item) => item.id === runId) || null;
  const selectedFinding = state.findings.find((item) => item.publicId === findingPublicId) || null;

  const ticks = useMemo(
    () => (scheduleId ? ticksForSchedule(state, scheduleId) : []),
    [scheduleId, state],
  );
  const tickRuns = useMemo(
    () => (scheduleId && scheduledAt ? runsForTick(state, scheduleId, scheduledAt) : []),
    [scheduleId, scheduledAt, state],
  );
  const findings = useMemo(
    () => (runId ? findingsForRun(state, runId) : []),
    [runId, state],
  );
  const windowIssues = useMemo(
    () => runIssueCardsInWindow(state, windowDays),
    [state, windowDays],
  );
  const timelineDays = useMemo(
    () => timelineDaysEndingAt(mockPulseNow(), windowDays),
    [windowDays],
  );
  const clampedRange = clampTimelineRange(timelineRange, timelineDays);
  const startDay = timelineDays[clampedRange[0]] || '';
  const endDay = timelineDays[clampedRange[1]] || '';
  const rangedIssues = useMemo(
    () => filterCardsByFoundDays(windowIssues, startDay, endDay),
    [endDay, startDay, windowIssues],
  );
  const rangedRuns = useMemo(
    () => uniqueRunsFromCards(rangedIssues),
    [rangedIssues],
  );
  const analysisKpis = useMemo(
    () => runAnalysisKpis(rangedRuns, rangedIssues),
    [rangedIssues, rangedRuns],
  );
  const analysisMarkers = useMemo(
    () => issueMapMarkers(rangedIssues),
    [rangedIssues],
  );
  const analysisOptions = useMemo(
    () => runAnalysisFilterOptions(rangedIssues),
    [rangedIssues],
  );
  const markCounts = useMemo(
    () => issueMarkCounts(windowIssues),
    [windowIssues],
  );
  const severityRows = useMemo(
    () => severityHeatRows(rangedIssues),
    [rangedIssues],
  );
  const severityHeat = useMemo(
    () => severityAgentCounts(rangedIssues),
    [rangedIssues],
  );
  const filteredIssues = useMemo(
    () => filterRunIssueCards(rangedIssues, {
      agentType, country, severity, runStatus,
    }),
    [agentType, country, rangedIssues, runStatus, severity],
  );

  const setQuery = (next: {
    agent?: string,
    scheduleId?: number,
    scheduledAt?: string,
    runId?: number,
    finding?: string,
    topicId?: number,
  }) => {
    const search = new URLSearchParams();
    const agent = next.agent ?? agentType;
    if (agent) {
      search.set('agent', agent);
    }
    if (next.scheduleId) {
      search.set('scheduleId', String(next.scheduleId));
    }
    if (next.scheduledAt) {
      search.set('scheduledAt', next.scheduledAt);
    }
    if (next.runId) {
      search.set('runId', String(next.runId));
    }
    if (next.finding) {
      search.set('finding', next.finding);
    }
    if (next.topicId) {
      search.set('topicId', String(next.topicId));
    }
    setParams(search);
  };

  const onMapCountryClick = (cellCountry: string) => {
    if (country === cellCountry) {
      setCountry('');
      return;
    }
    setCountry(cellCountry);
  };

  const onWindowChange = (days: RunAnalysisSpanDays) => {
    setWindowDays(days);
    setTimelineRange([0, days - 1]);
  };

  const onTimelineMarkClick = (day: string) => {
    const index = timelineDays.indexOf(day);
    if (index < 0) {
      return;
    }
    if (clampedRange[0] === index && clampedRange[1] === index) {
      setTimelineRange(fullTimelineRange(timelineDays));
      return;
    }
    setTimelineRange([index, index]);
  };

  const onSeverityAgentClick = (rowSeverity: string, cellAgent: string) => {
    if (severity === rowSeverity && agentType === cellAgent) {
      setSeverity('');
      setQuery({ agent: '' });
      return;
    }
    setSeverity(rowSeverity);
    setQuery({ agent: cellAgent });
  };

  const onOpenIssue = (card: RunIssueCard) => {
    setQuery({
      agent: agentType,
      scheduleId: card.run.scheduleId,
      scheduledAt: card.run.scheduledAt,
      runId: card.run.id,
      finding: card.finding.publicId,
      topicId: card.run.watchTopicId || undefined,
    });
  };

  const kindLabel = (kind: string) => {
    const key = KIND_I18N[kind];
    return key ? t(`targetedMarketing.${key}`) : kind;
  };

  const statusPill = (status: string) => (
    <span className={`${styles.statusPill} ${statusClass(status)}`}>{status}</span>
  );

  const failedNote = (item: WatchRun) => {
    if (item.status !== 'failed') {
      return null;
    }
    return (
      <p className={styles.muted}>{item.errorMessage || t('targetedMarketing.runFailed')}</p>
    );
  };

  const renderPayload = (finding: WatchFinding) => {
    const entries = payloadEntries(finding.payloadJson);
    return (
      <Panel className={styles.panel}>
        <h2 className={styles.sectionTitle}>{finding.title}</h2>
        {finding.description ? <p>{finding.description}</p> : null}
        {entries.length === 0 ? (
          <EmptyList text={t('targetedMarketing.noPayload')} />
        ) : (
          <dl className={styles.payloadList}>
            {entries.map((entry) => (
              <div key={entry.key} className={styles.payloadRow}>
                <dt>{entry.key}</dt>
                <dd>{entry.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Panel>
    );
  };

  const level = drillLevel(Boolean(schedule), scheduledAt, Boolean(run));

  return (
    <WatchTowerShell primary="runs" runsSubView="runs" actions={null}>
      <nav className={styles.crumbList} aria-label={t('targetedMarketing.runsCrumb')}>
        <button
          type="button"
          className={styles.crumbButton}
          onClick={() => setQuery({ agent: agentType })}
        >
          {t('targetedMarketing.runsTitle')}
        </button>
        {schedule ? (
          <>
            <span className={styles.crumbSep}>/</span>
            <button
              type="button"
              className={styles.crumbButton}
              onClick={() => setQuery({ scheduleId: schedule.id })}
            >
              {schedule.title}
            </button>
          </>
        ) : null}
        {scheduledAt ? (
          <>
            <span className={styles.crumbSep}>/</span>
            <button
              type="button"
              className={styles.crumbButton}
              onClick={() => setQuery({ scheduleId, scheduledAt })}
            >
              {formatIsoDateTime(scheduledAt)}
            </button>
          </>
        ) : null}
        {run ? (
          <>
            <span className={styles.crumbSep}>/</span>
            <button
              type="button"
              className={styles.crumbButton}
              onClick={() => setQuery({ scheduleId, scheduledAt, runId: run.id })}
            >
              {resolveTargetName(
                state,
                run.watchCompanyId,
                run.watchTargetKind,
                run.watchTargetId,
              )}
            </button>
          </>
        ) : null}
      </nav>

      <WatchTowerRunAnalysis
        windowDays={windowDays}
        agentType={agentType}
        country={country}
        severity={severity}
        runStatus={runStatus}
        countries={analysisOptions.countries}
        severities={analysisOptions.severities}
        kpis={analysisKpis}
        markers={analysisMarkers}
        timelineDays={timelineDays}
        timelineRange={clampedRange}
        markCounts={markCounts}
        severityRows={severityRows}
        severityAgentCounts={severityHeat}
        rangeIssues={rangedIssues}
        issues={filteredIssues}
        onWindowChange={onWindowChange}
        onAgentChange={(type) => setQuery({ agent: type })}
        onCountryChange={setCountry}
        onSeverityChange={setSeverity}
        onRunStatusChange={setRunStatus}
        onMapCountryClick={onMapCountryClick}
        onTimelineRangeChange={setTimelineRange}
        onTimelineMarkClick={onTimelineMarkClick}
        onSeverityAgentClick={onSeverityAgentClick}
        onOpenIssue={onOpenIssue}
      />

      {level === 2 ? (
        <Panel className={styles.panel}>
          <h2 className={styles.sectionTitle}>{t('targetedMarketing.levelTick')}</h2>
          {ticks.length === 0 ? (
            <EmptyList text={t('targetedMarketing.emptyTicks')} />
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('targetedMarketing.tickTime')}</th>
                    <th>{t('targetedMarketing.agents')}</th>
                    <th>{t('targetedMarketing.status')}</th>
                    <th>{t('targetedMarketing.targetsCount')}</th>
                    <th>{t('targetedMarketing.elevatedCount')}</th>
                    <th>{t('targetedMarketing.cost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ticks.map((tick) => (
                    <tr key={`${tick.scheduleId}|${tick.scheduledAt}`}>
                      <td>
                        <button
                          type="button"
                          className={styles.nameButton}
                          onClick={() => setQuery({
                            scheduleId: tick.scheduleId,
                            scheduledAt: tick.scheduledAt,
                          })}
                        >
                          {formatIsoDateTime(tick.scheduledAt)}
                        </button>
                      </td>
                      <td>{formatAgentType(tick.type)}</td>
                      <td>{statusPill(tick.status)}</td>
                      <td>{tick.targetCount}</td>
                      <td>{tick.elevatedCount}</td>
                      <td>{formatCostUsd(tick.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ) : null}

      {level === 3 ? (
        <Panel className={styles.panel}>
          <h2 className={styles.sectionTitle}>{t('targetedMarketing.levelTarget')}</h2>
          {tickRuns.length === 0 ? (
            <EmptyList text={t('targetedMarketing.emptyTargets')} />
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('targetedMarketing.target')}</th>
                    <th>{t('targetedMarketing.targetKind')}</th>
                    <th>{t('targetedMarketing.status')}</th>
                    <th>{t('targetedMarketing.findings')}</th>
                    <th>{t('targetedMarketing.elevatedCount')}</th>
                    <th>{t('targetedMarketing.duration')}</th>
                    <th>{t('targetedMarketing.tokens')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tickRuns.map((item) => {
                    const runFindings = findingsForRun(state, item.id);
                    return (
                      <tr key={item.id}>
                        <td>
                          <button
                            type="button"
                            className={styles.nameButton}
                            onClick={() => setQuery({
                              scheduleId,
                              scheduledAt,
                              runId: item.id,
                            })}
                          >
                            {resolveTargetName(
                              state,
                              item.watchCompanyId,
                              item.watchTargetKind,
                              item.watchTargetId,
                            )}
                          </button>
                        </td>
                        <td>{kindLabel(item.watchTargetKind)}</td>
                        <td>
                          {statusPill(item.status)}
                          {failedNote(item)}
                        </td>
                        <td>{runFindings.length}</td>
                        <td>{elevatedFindingCount(runFindings)}</td>
                        <td>{formatDurationMs(item.durationMs)}</td>
                        <td>{tokenCountLabel(item)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ) : null}

      {level === 4 ? (
        <>
          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.levelFinding')}</h2>
            {run?.status === 'failed' ? failedNote(run) : null}
            {findings.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyFindings')} />
            ) : (
              <div className={styles.tableWrap}>
                <table className={`${styles.table} ${styles.tableCompact}`}>
                  <thead>
                    <tr>
                      <th>{t('targetedMarketing.severity')}</th>
                      <th>{t('targetedMarketing.sourceKind')}</th>
                      <th>{t('targetedMarketing.findingType')}</th>
                      <th>{t('targetedMarketing.title')}</th>
                      <th>{t('targetedMarketing.observedAt')}</th>
                      <th>{t('targetedMarketing.confidence')}</th>
                      <th>{t('targetedMarketing.sourceUrl')}</th>
                      <th>{t('targetedMarketing.payloadPreview')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((finding) => (
                      <tr key={finding.publicId}>
                        <td>
                          <span className={severityClass(finding.severity)}>
                            {finding.severity}
                          </span>
                        </td>
                        <td>
                          {t(`targetedMarketing.sourceKind_${findingSourceKind(finding)}`)}
                          {' · '}
                          {findingSourceLabel(state, finding)}
                        </td>
                        <td>{finding.findingType}</td>
                        <td>
                          <button
                            type="button"
                            className={styles.nameButton}
                            onClick={() => setQuery({
                              scheduleId,
                              scheduledAt,
                              runId,
                              finding: finding.publicId,
                            })}
                          >
                            {finding.title}
                          </button>
                        </td>
                        <td>{formatIsoDateTime(finding.observedAt)}</td>
                        <td>{formatConfidence(finding.confidenceScore)}</td>
                        <td>
                          {finding.sourceUrl ? (
                            <a
                              href={finding.sourceUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t('targetedMarketing.openSource')}
                            </a>
                          ) : t('targetedMarketing.none')}
                        </td>
                        <td>{payloadPreview(finding.payloadJson)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
          {selectedFinding ? renderPayload(selectedFinding) : (
            <p className={styles.muted}>{t('targetedMarketing.payloadHint')}</p>
          )}
        </>
      ) : null}
    </WatchTowerShell>
  );
}

export default WatchTowerRuns;
